import { App } from '../admin.js';
import { TabbedModal } from './tabbed-modal.js';
import { contratoModal } from './modals/contrato-modal-template.js';
// SSOT de canales y de las reglas puras de visibilidad/armado — la misma que ejecutan los tests.
import {
    CANALES_DE_COMISION_DEL_MODAL,
    canalEsVisible,
    construirComisionPorCanal,
} from '@shared/config/games/lotowheel/canales-de-comision-del-contrato.js';
import { t } from '../services/i18n-admin.js';
import { escapeHtml } from '@shared/utils/escape-html.js';
import {
    htmlDeCabeceraDeCategoria,
    htmlDeIncentivoScoped,
    htmlDeTarjetaDePresetClonable,
} from './contrato-modal-incentivos-html.js';
import { icon } from '../utils/icon.js';
// SSOT de las reglas de techo por canal — la misma que ejecuta el validador del backend, importada
// (no reimplementada) para que el aviso del modal y el veredicto del backend no puedan divergir.
import { detectarViolacionDeTechoPorCanal } from '@shared/config/games/lotowheel/comision-techo-por-canal.js';

/**
 * ContratoModal — modal unificado de gestión de contratos contables.
 *
 * Un contrato vive entre dos nodos (parent/child) y define las reglas
 * económicas de su relación. Modal con tabs (TabbedModal) para 6
 * dominios: comisiones, wallet, incentivos, adelantos, fondo, conformidad.
 *
 * Mapa de secciones (orden del archivo):
 *    1. API PÚBLICA            open() / close() / save() — entrypoint del modal.
 *    2. TABS                   render de las pestañas vía TabbedModal.
 *    3. INCENTIVOS COMERCIALES promociones scoped al nodo (CRUD inline).
 *    4. INCENTIVOS CREAR       wizard crear/clonar preset de incentivo.
 *    5. OBLIGACIONES           impuestos/fees/deducciones del contrato.
 *    6. PRIVADOS SETUP         init de inputs y bindings al abrir.
 *    7. PRIVADOS UI HELPERS    formatters, toggles, validaciones inline.
 *    8. PRIVADOS API CALLS     fetches al backend agrupados.
 *
 * Endpoints base: /api/admin/contratos, /api/admin/contratos/:id/{tabs}.
 * Componente reusado: TabbedModal (tabs uniformes en modal genérico).
 */

export const ContratoModal = {
    // Estado
    _mode: 'create', // 'create' | 'edit'
    _contratoId: null,
    _hijoEntidad: null,
    _hijoId: null,
    _hijoNombre: null,
    _padreEntidad: null,
    _padreId: null,
    _heredadoDe: null,

    // Callbacks
    _onSaveCallback: null,

    // ═══════════════════════════════════════════════════════════════════════
    // API PÚBLICA — open() / close() / save() entrypoints del modal.
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Abre el modal para crear un nuevo contrato
     * @param {Object} options
     * @param {string} options.hijoEntidad - 'operador' | 'representante' | 'punto_venta'
     * @param {number} options.hijoId - ID de la entidad hijo
     * @param {string} options.hijoNombre - Nombre para mostrar
     * @param {Function} options.onSave - Callback al guardar
     */
    async openCreate(options) {
        this._mode = 'create';
        this._contratoId = null;
        this._hijoEntidad = options.hijoEntidad;
        this._hijoId = options.hijoId;
        this._hijoNombre = options.hijoNombre || t('Entidad');
        this._onSaveCallback = options.onSave || null;
        this._heredadoDe = null;
        this._capacidades = options.capacidades || null; // { wallet, tickets } — qué canales de % mostrar
        this._esPromotorActual = !!options.esPromotor; // promotor = solo wallet (no ticket)

        // Padre + config efectiva en paralelo — independientes entre sí.
        const [padreInfo, efectivo] = await Promise.all([
            this._fetchPadreInfo(this._hijoEntidad, this._hijoId),
            this._fetchContratoEfectivo(this._hijoEntidad, this._hijoId),
        ]);
        // El endpoint /api/admin/contabilidad/padre devuelve { nodo, esRaiz? }.
        // Si esRaiz=true → no hay padre (nodo raíz) → entidad = gameType de la brand.
        // Hoy las 2 brands modeladas (loterianacional + roletanacional) son loto37.
        // Cuando aparezca brand con gameType distinto, leer de import.meta.env o
        // de un endpoint /api/brand-info en vez de hardcoded.
        const padreNodo = padreInfo?.nodo || null;
        this._padreEntidad = padreNodo?.tipo || 'loto37';
        this._padreId = padreNodo?.id || null;
        // Techo del padre POR CANAL ({canal: {porcentaje, base}}): el guard compara canal contra canal,
        // que es como valida el backend. null = el padre no tiene contrato propio.
        this._padreTechoPorCanal = padreInfo?.techo_por_canal_padre ?? null;

        // Configurar UI (soloPromotor filtra las tabs a solo "Comisiones")
        this._setupModal(!!options.esPromotor);
        this._populateForm(efectivo);

        // Mostrar indicador de herencia si aplica
        if (efectivo.heredado) {
            this._heredadoDe = efectivo.heredado_de;
            this._showHerenciaIndicator(efectivo.heredado_de);
        }

        // Título
        document.getElementById('modalContratoTitle').textContent = t('Nuevo Contrato - ') + this._hijoNombre;
        document.getElementById('contratoSaveBtn').textContent = t('Crear Contrato');

        App.openModal('modalContrato');
    },

    /**
     * Abre el modal para editar un contrato existente
     * @param {Object} options
     * @param {number} options.contratoId - ID del contrato
     * @param {string} options.hijoNombre - Nombre para mostrar
     * @param {Function} options.onSave - Callback al guardar
     */
    async openEdit(options) {
        this._mode = 'edit';
        this._contratoId = options.contratoId;
        this._hijoNombre = options.hijoNombre || t('Entidad');
        this._onSaveCallback = options.onSave || null;
        this._heredadoDe = null;
        this._capacidades = options.capacidades || null; // { wallet, tickets } — qué canales de % mostrar
        this._esPromotorActual = !!options.esPromotor; // promotor = solo wallet (no ticket)

        // Cargar contrato
        const contrato = await this._fetchContrato(this._contratoId);
        if (!contrato) {
            App.toast(t('Contrato no encontrado'), 'error');
            return;
        }

        this._hijoEntidad = contrato.hijo_entidad;
        // El contrato usa nodo_id (Nodos Fractales v4.0), hijo_id es alias legacy
        this._hijoId = contrato.nodo_id || contrato.hijo_id;
        this._padreEntidad = contrato.padre_entidad;
        this._padreId = contrato.padre_id;

        // Techo del padre para prevenir la inversión hijo>padre TAMBIÉN al editar (no solo al crear).
        // Sin esto, el singleton conservaba el valor de un create previo (validación contra el padre
        // equivocado) o quedaba null (guard saltado). El backend lo rechaza igual, pero la UX debe avisar.
        const padreInfoEdicion = await this._fetchPadreInfo(this._hijoEntidad, this._hijoId);
        this._padreTechoPorCanal = padreInfoEdicion?.techo_por_canal_padre ?? null;

        // Configurar UI (soloPromotor filtra las tabs a solo "Comisiones")
        this._setupModal(!!options.esPromotor);
        this._populateForm(contrato);

        // Título
        document.getElementById('modalContratoTitle').textContent = t('Editar Contrato - ') + this._hijoNombre;
        document.getElementById('contratoSaveBtn').textContent = t('Guardar Cambios');

        App.openModal('modalContrato');
    },

    /**
     * Guarda el contrato (crear o actualizar)
     */
    async save() {
        const datos = this._collectFormData();

        // Cada canal declarado debe traer un % válido 0-100. No hay "% general": un contrato pacta
        // canales (SPEC_comision-solo-por-canal) y el backend exige al menos 1 vía
        // exigirAlMenosUnCanalDeComision — un canal con valor fuera de rango se ataja acá.
        const canalFueraDeRango = this._detectarPorcentajeDeCanalInvalido(datos.comision_por_canal);
        if (canalFueraDeRango) {
            App.toast(
                t('El porcentaje del canal debe ser un número entre 0 y 100') + ` — ${canalFueraDeRango}`,
                'error'
            );
            return;
        }

        // Techo por canal contra el nivel superior. Las reglas viven en shared/ y son las MISMAS que
        // ejecuta el backend al escribir: avisamos acá para no frustrar al operador con un 400 tardío,
        // pero la autoridad sigue siendo el backend (fail-closed).
        const violacion = detectarViolacionDeTechoPorCanal(datos.comision_por_canal, this._padreTechoPorCanal);
        if (violacion) {
            const mensaje =
                violacion.motivo === 'zona_salteada'
                    ? t('Base inválida: el nivel superior cobra sobre lo jugado en este canal') +
                      ` — ${violacion.canal}`
                    : t('La comisión del canal no puede superar la del nivel superior') +
                      ` — ${violacion.canal}: ${violacion.pctHijo}% > ${violacion.techoSuperior}%`;
            App.toast(mensaje, 'error');
            return;
        }

        try {
            let result;
            if (this._mode === 'create') {
                result = await this._createContrato(datos);
                App.toast(t('Contrato creado correctamente'), 'success');
            } else {
                result = await this._updateContrato(datos);
                App.toast(t('Contrato actualizado correctamente'), 'success');
            }

            App.closeModal();

            if (this._onSaveCallback) {
                this._onSaveCallback(result);
            }
        } catch (error) {
            console.error('[ContratoModal] Error al guardar:', error);
            App.toast(error.message || t('Error al guardar contrato'), 'error');
        }
    },

    /**
     * Devuelve el nombre del primer canal cuyo % no sea un número 0-100, o null si todos son válidos.
     * Un canal sin valor no llega acá (_collectComisionPorCanal ya lo omite del mapa = no se pacta).
     * @param {Object|null|undefined} porCanal
     * @returns {string|null}
     */
    _detectarPorcentajeDeCanalInvalido(porCanal) {
        if (!porCanal) return null;
        for (const canal of Object.keys(porCanal)) {
            const pct = Number(porCanal[canal]?.porcentaje);
            if (!Number.isFinite(pct) || pct < 0 || pct > 100) return canal;
        }
        return null;
    },

    // ═══════════════════════════════════════════════════════════════════════
    // TABS — render de las 6 pestañas del modal vía TabbedModal.
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Cambia de tab
     * @param {string} tabId
     */
    setTab(tabId) {
        TabbedModal.setTab('modalContrato', tabId);
    },

    // ═══════════════════════════════════════════════════════════════════════
    // INCENTIVOS COMERCIALES — promociones scoped al nodo (CRUD inline).
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Carga y muestra las promociones scoped a este nodo
     */
    async loadIncentivos() {
        const container = document.getElementById('incentivosListContainer');
        if (!container) return;

        container.innerHTML = `<div style="text-align:center; padding:20px; color:var(--text-tertiary);">${t('Cargando...')}</div>`;

        try {
            const nodoId = this._hijoId;
            const res = await fetch(`/api/admin/marketing/promociones?nodoId=${nodoId}`, {
                headers: { 'x-admin-token': App.getToken() },
            });

            if (!res.ok) throw new Error(t('Error al cargar incentivos'));

            const data = await res.json();
            const promociones = data.definiciones || [];

            // Filtrar solo las scoped a este nodo
            const scopedPromos = promociones.filter(p => p.scope_nodo_id === nodoId);

            if (scopedPromos.length === 0) {
                container.innerHTML = `
                    <div style="text-align:center; padding:30px; color:var(--text-tertiary);">
                        <p style="margin-bottom:12px;">${t('No hay incentivos comerciales para esta entidad')}</p>
                        <p style="font-size:12px;">${t('Usa el modulo de Marketing para crear promociones scoped a este nodo')}</p>
                    </div>`;
                return;
            }

            let html = '<div style="display:flex; flex-direction:column; gap:8px;">';
            for (let i = 0; i < scopedPromos.length; i++) html += htmlDeIncentivoScoped(scopedPromos[i]);
            html += '</div>';
            container.innerHTML = html;
        } catch (err) {
            container.innerHTML = `<div style="text-align:center; padding:20px; color:var(--accent-red);">${escapeHtml(err.message)}</div>`;
        }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // INCENTIVOS CREAR — wizard para crear/clonar preset de incentivo.
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Abre el modal de Marketing para crear una promo scoped a este nodo
     */
    abrirCrearPromoScoped() {
        if (typeof MarketingModule === 'undefined') {
            App.toast(t('Modulo de Marketing no disponible'), 'error');
            return;
        }
        MarketingModule.abrirModalCrearScoped(this._hijoId, () => {
            this.loadIncentivos();
        });
    },

    /**
     * Abre el modal de Marketing para editar un incentivo existente del nodo.
     * Al guardar se refresca la lista del tab (mismo contrato que crear).
     */
    editarPromoScoped(promoId) {
        if (typeof MarketingModule === 'undefined') {
            App.toast(t('Modulo de Marketing no disponible'), 'error');
            return;
        }
        MarketingModule.abrirModalEditar(promoId, () => {
            this.loadIncentivos();
        });
    },

    /**
     * Muestra selector inline de presets para clonar scoped a este nodo
     */
    async abrirClonarPreset() {
        const container = document.getElementById('incentivosListContainer');
        if (!container) return;

        container.innerHTML = `<div style="text-align:center; padding:20px; color:var(--text-tertiary);">${t('Cargando plantillas...')}</div>`;

        try {
            const res = await fetch('/api/admin/marketing/presets', {
                headers: { 'x-admin-token': App.getToken() },
            });
            if (!res.ok) throw new Error(t('Error al cargar presets'));
            const data = await res.json();
            const presets = data.presets || [];

            // Filtrar solo presets orientados a nodos (destino != 'cliente')
            // En contratos, los incentivos son para el nodo, no para jugadores
            const DESTINOS_NODO = ['referente', 'arbol_ascendente', 'nodos_en_red'];
            const presetsNodo = presets.filter(p => {
                try {
                    const recompensas =
                        typeof p.recompensas_json === 'string' ? JSON.parse(p.recompensas_json) : p.recompensas_json;
                    if (!Array.isArray(recompensas)) return false;
                    return recompensas.some(r => DESTINOS_NODO.indexOf(r.destino) !== -1);
                } catch (_) {
                    return false;
                }
            });

            if (presetsNodo.length === 0) {
                container.innerHTML = `
                    <div style="text-align:center; padding:20px; color:var(--text-tertiary);">
                        <p>${t('No hay plantillas de incentivos para nodos')}</p>
                        <button class="btn btn-sm" data-action="contrato:loadIncentivos" style="font-size:11px; margin-top:8px;">${t('Volver')}</button>
                    </div>`;
                return;
            }

            // Agrupar por categoria
            const porCategoria = {};
            for (let i = 0; i < presetsNodo.length; i++) {
                const cat = presetsNodo[i].categoria || 'otros';
                if (!porCategoria[cat]) porCategoria[cat] = [];
                porCategoria[cat].push(presetsNodo[i]);
            }

            let html = '<div style="display:flex; flex-direction:column; gap:6px;">';
            html += '<div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">';
            html += `<strong style="font-size:13px;">${t('Selecciona una plantilla')}</strong>`;
            html += `<button class="btn btn-sm" data-action="contrato:loadIncentivos" style="font-size:11px;">${t('Volver')}</button>`;
            html += '</div>';

            const categorias = Object.keys(porCategoria);
            for (let c = 0; c < categorias.length; c++) {
                const cat = categorias[c];
                const items = porCategoria[cat];
                html += `<div style="margin-bottom:4px;">`;
                html += htmlDeCabeceraDeCategoria(cat);
                for (let i = 0; i < items.length; i++) html += htmlDeTarjetaDePresetClonable(items[i]);
                html += '</div>';
            }
            html += '</div>';
            container.innerHTML = html;
        } catch (err) {
            container.innerHTML = `<div style="text-align:center; padding:20px; color:var(--accent-red);">${escapeHtml(err.message)}</div>`;
        }
    },

    /**
     * Clona un preset con scope_nodo_id de este nodo
     */
    async clonarPresetScoped(presetId, presetNombre) {
        try {
            const res = await fetch(`/api/admin/marketing/presets/${presetId}/clonar`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-token': App.getToken() },
                body: JSON.stringify({ scope_nodo_id: this._hijoId }),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || t('Error al clonar preset'));
            }

            App.toast(`${t('Promoción activada')}: "${presetNombre}"`, 'success');
            this.loadIncentivos();
        } catch (err) {
            App.toast(err.message || t('Error al clonar preset'), 'error');
        }
    },

    /**
     * Elimina una promocion scoped a este nodo (hard delete)
     */
    async eliminarPromoScoped(promoId, promoNombre) {
        if (!confirm(`${t('¿Eliminar incentivo "')}${promoNombre}"?`)) return;

        try {
            const res = await fetch(`/api/admin/marketing/promociones/${promoId}`, {
                method: 'DELETE',
                headers: { 'x-admin-token': App.getToken() },
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || t('Error al eliminar'));
            }

            App.toast(`${t('Incentivo eliminado')}: "${promoNombre}"`, 'success');
            this.loadIncentivos();
        } catch (err) {
            App.toast(err.message || t('Error al eliminar'), 'error');
        }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // OBLIGACIONES — impuestos, fees, deducciones del contrato.
    // ═══════════════════════════════════════════════════════════════════════

    async loadObligaciones() {
        const container = document.getElementById('obligacionesListContainer');
        if (!container) return;

        if (!this._contratoId && !this._hijoId) {
            container.innerHTML = `<div style="text-align:center; padding:20px; color:var(--text-tertiary);">${t('Guarde el contrato primero para gestionar obligaciones.')}</div>`;
            return;
        }

        container.innerHTML = `<div style="text-align:center; padding:20px; color:var(--text-tertiary);">${t('Cargando...')}</div>`;

        try {
            // Usar endpoint /efectivas si tenemos nodoId — incluye propias + heredadas
            // Si no hay contrato propio aún, muestra las heredadas como referencia (read-only)
            let obligaciones = [];
            let propiaIds = new Set();

            if (this._hijoId) {
                const resEfectivas = await fetch(`/api/admin/obligaciones/${this._hijoId}/efectivas`, {
                    headers: { 'x-admin-token': App.getToken() },
                });
                if (resEfectivas.ok) {
                    const dataEf = await resEfectivas.json();
                    obligaciones = dataEf.obligaciones || [];
                }
            }

            // Si tiene contrato propio, identificar cuáles son del contrato actual (eliminables)
            if (this._contratoId) {
                const resPropia = await fetch(`/api/admin/contratos/${this._contratoId}/obligaciones`, {
                    headers: { 'x-admin-token': App.getToken() },
                });
                if (resPropia.ok) {
                    const dataPropia = await resPropia.json();
                    for (let i = 0; i < (dataPropia.obligaciones || []).length; i++) {
                        propiaIds.add(dataPropia.obligaciones[i].id);
                    }
                }
            }

            if (obligaciones.length === 0) {
                container.innerHTML = `<div style="text-align:center; padding:20px; color:var(--text-tertiary);">${t('Sin obligaciones configuradas.')}</div>`;
                return;
            }

            const tipoLabels = {
                impuesto_estatal: t('Imp. Estatal'),
                impuesto_municipal: t('Imp. Municipal'),
                fee_plataforma: t('Fee Plataforma'),
                regalia: t('Regalía'),
                pozo_comun: t('Pozo Común'),
                mantenimiento: t('Mantenimiento'),
                otro: t('Otro'),
            };

            let html = '<table style="width:100%; font-size:13px; border-collapse:collapse;">';
            html += '<thead><tr style="border-bottom:1px solid var(--border-color); color:var(--text-secondary);">';
            html += `<th style="text-align:left; padding:6px 4px;">${t('Nombre')}</th>`;
            html += `<th style="text-align:left; padding:6px 4px;">${t('Tipo')}</th>`;
            html += `<th style="text-align:center; padding:6px 4px;">${t('Base')}</th>`;
            html += `<th style="text-align:right; padding:6px 4px;">%</th>`;
            html += '<th style="width:32px;"></th>';
            html += '</tr></thead><tbody>';

            for (let i = 0; i < obligaciones.length; i++) {
                const ob = obligaciones[i];
                // Es heredada si tenemos contrato propio pero este ID no está en él,
                // o si el campo heredado viene explícito del endpoint /efectivas
                const esHeredada = ob.es_heredada || (this._contratoId && !propiaIds.has(ob.id));
                const rowStyle = esHeredada
                    ? 'border-bottom:1px solid var(--border-color); opacity:0.65;'
                    : 'border-bottom:1px solid var(--border-color);';

                html += `<tr style="${rowStyle}">`;
                html += `<td style="padding:8px 4px;">
                    <strong>${ob.nombre}</strong>
                    <br><span style="color:var(--text-tertiary); font-size:11px;">${ob.codigo}${esHeredada ? ` <span style="color:var(--accent-blue);opacity:0.8;">· ${t('heredada')}</span>` : ''}</span>
                </td>`;
                html += `<td style="padding:8px 4px; color:var(--text-secondary); font-size:12px;">${tipoLabels[ob.tipo] || ob.tipo}</td>`;
                html += `<td style="padding:8px 4px; text-align:center; font-size:12px;">${ob.base}</td>`;
                html += `<td style="padding:8px 4px; text-align:right; font-weight:600;">${ob.porcentaje}%</td>`;
                // Solo mostrar botón eliminar en obligaciones propias del contrato actual
                html += `<td style="padding:8px 4px; text-align:center;">`;
                if (!esHeredada && this._contratoId) {
                    html += `<button class="btn-icon" data-action="contrato:eliminarObligacion" data-id="${ob.id}" data-nombre="${ob.nombre.replace(/"/g, '&quot;')}" title="${t('Eliminar')}">${icon('trash-2', '', { style: 'width:14px; opacity:0.5;' })}</button>`;
                }
                html += `</td></tr>`;
            }

            html += '</tbody></table>';
            container.innerHTML = html;
        } catch (err) {
            console.error('[ContratoModal] Error cargando obligaciones:', err);
            container.innerHTML = `<div style="text-align:center; padding:20px; color:var(--text-danger);">${t('Error al cargar obligaciones')}</div>`;
        }
    },

    toggleObligacionForm() {
        const form = document.getElementById('obligacionFormContainer');
        if (!form) return;
        const visible = form.style.display !== 'none';
        if (visible) {
            form.style.display = 'none';
        } else {
            form.style.display = 'block';
            // Siempre arrancar desde el paso 1 al abrir
            this._goToWizardStep(1);
            // Seleccionar visualmente la primera card de tipo por defecto
            this.onTipoCardClick(
                document.querySelector('.ob-tipo-card[data-value="impuesto_estatal"]') || {
                    dataset: { value: 'impuesto_estatal' },
                }
            );
            // Seleccionar visualmente la primera card de cálculo por defecto
            this.onCalculoCardClick(
                document.querySelector('.ob-calculo-card[data-value="porcentaje"]') || {
                    dataset: { value: 'porcentaje' },
                }
            );
        }
    },

    // ── Wizard de obligaciones ─────────────────────────────────────────────

    _goToWizardStep(stepNumber) {
        // Paneles de contenido
        for (let i = 1; i <= 4; i++) {
            const panel = document.getElementById(`obligacionStep${i}`);
            if (panel) panel.style.display = i === stepNumber ? 'block' : 'none';
        }
        // Pestañas indicadoras
        const tabs = document.querySelectorAll('.ob-step-tab');
        for (let i = 0; i < tabs.length; i++) {
            const tabStep = parseInt(tabs[i].dataset.step, 10);
            const isActive = tabStep === stepNumber;
            const isCompleted = tabStep < stepNumber;
            tabs[i].style.borderBottomColor = isActive ? 'var(--accent-blue)' : 'transparent';
            tabs[i].style.color = isActive
                ? 'var(--accent-blue)'
                : isCompleted
                  ? 'var(--text-primary)'
                  : 'var(--text-tertiary)';
        }
        this._currentWizardStep = stepNumber;
        if (stepNumber === 4) this._actualizarResumenObligacion();
    },

    wizardNext() {
        const step = this._currentWizardStep || 1;
        if (step < 4) this._goToWizardStep(step + 1);
    },

    wizardPrev() {
        const step = this._currentWizardStep || 1;
        if (step > 1) this._goToWizardStep(step - 1);
    },

    // Clic en tarjeta de tipo (paso 1)
    onTipoCardClick(element) {
        const valor = element.dataset.value;
        if (!valor) return;

        // Marcar card seleccionada visualmente
        const cards = document.querySelectorAll('.ob-tipo-card');
        for (let i = 0; i < cards.length; i++) {
            const isSelected = cards[i].dataset.value === valor;
            cards[i].style.borderColor = isSelected ? 'var(--accent-blue)' : '';
            cards[i].style.background = isSelected ? 'rgba(59,130,246,0.15)' : '';
            // Actualizar opacidad del icono y color del texto
            const img = cards[i].querySelector('img');
            const span = cards[i].querySelector('span');
            if (img) img.style.opacity = isSelected ? '1' : '0.7';
            if (span) span.style.color = isSelected ? 'var(--accent-blue)' : '';
        }

        // Guardar valor en campo hidden
        const tipoHidden = document.getElementById('obligacionTipo');
        if (tipoHidden) tipoHidden.value = valor;

        // Mostrar/ocultar input de texto libre para "otro"
        const otroBlock = document.getElementById('obligacionTipoOtroBlock');
        if (otroBlock) otroBlock.style.display = valor === 'otro' ? 'block' : 'none';
    },

    // Clic en tarjeta de tipo de cálculo (paso 3)
    onCalculoCardClick(element) {
        const valor = element.dataset.value;
        if (!valor) return;

        // Marcar card seleccionada
        const cards = document.querySelectorAll('.ob-calculo-card');
        for (let i = 0; i < cards.length; i++) {
            const isSelected = cards[i].dataset.value === valor;
            cards[i].style.borderColor = isSelected ? 'var(--accent-blue)' : '';
            cards[i].style.background = isSelected ? 'rgba(59,130,246,0.15)' : 'var(--bg-tertiary)';
        }

        // Guardar en hidden
        const calculoHidden = document.getElementById('obligacionCalculoTipo');
        if (calculoHidden) calculoHidden.value = valor;

        // Mostrar/ocultar campos según tipo
        const pctGroup = document.getElementById('obligacionPorcentajeGroup');
        const fijoGroup = document.getElementById('obligacionMontoFijoGroup');
        const fijoLabel = document.getElementById('obligacionMontoFijoLabel');
        if (!pctGroup || !fijoGroup) return;

        pctGroup.style.display = valor === 'fijo' ? 'none' : 'block';
        fijoGroup.style.display = valor === 'porcentaje' ? 'none' : 'block';

        if (fijoLabel) {
            if (valor === 'mayor_de')
                fijoLabel.innerHTML = `${t('Monto fijo mínimo')} <span style="font-weight:400;color:var(--text-tertiary);">${t('(en centavos)')}</span>`;
            else if (valor === 'menor_de')
                fijoLabel.innerHTML = `${t('Monto fijo máximo')} <span style="font-weight:400;color:var(--text-tertiary);">${t('(en centavos)')}</span>`;
            else
                fijoLabel.innerHTML = `${t('Monto fijo')} <span style="font-weight:400;color:var(--text-tertiary);">${t('(en centavos)')}</span>`;
        }
    },

    // Cambio en radio de absorbe (paso 4)
    _onObligacionAbsorbeChange() {
        const radios = document.querySelectorAll('input[name="obligacionAbsorbe"]');
        let absorbeValue = 'operacion';
        for (let i = 0; i < radios.length; i++) {
            if (radios[i].checked) {
                absorbeValue = radios[i].value;
                break;
            }
        }
        const block = document.getElementById('obligacionAbsorbeCondicionalBlock');
        if (block) block.style.display = absorbeValue === 'condicional' ? 'block' : 'none';
    },

    // Resumen legible antes de guardar (paso 4)
    _actualizarResumenObligacion() {
        const resumenEl = document.getElementById('obligacionResumen');
        if (!resumenEl) return;

        const codigo = (document.getElementById('obligacionCodigo')?.value || '').trim().toUpperCase() || '—';
        const nombre = (document.getElementById('obligacionNombre')?.value || '').trim() || '—';

        const tipoHidden = document.getElementById('obligacionTipo');
        const tipoValor = tipoHidden?.value || 'impuesto_estatal';
        const tipoOtroTexto = (document.getElementById('obligacionTipoOtroTexto')?.value || '').trim();
        const tipoLabel = tipoValor === 'otro' && tipoOtroTexto ? tipoOtroTexto : tipoValor.replace(/_/g, ' ');

        const baseRadios = document.querySelectorAll('input[name="obligacionBase"]');
        let baseValor = 'ggr';
        for (let i = 0; i < baseRadios.length; i++) {
            if (baseRadios[i].checked) {
                baseValor = baseRadios[i].value;
                break;
            }
        }
        const baseNeta = document.getElementById('obligacionBaseNeta')?.checked;

        const calculoTipo = document.getElementById('obligacionCalculoTipo')?.value || 'porcentaje';
        const pct = document.getElementById('obligacionPorcentaje')?.value || '0';
        const fijo = document.getElementById('obligacionMontoFijo')?.value || '';

        let importeTexto = '';
        if (calculoTipo === 'porcentaje') importeTexto = `${pct}% ${t('sobre')} ${baseValor}`;
        else if (calculoTipo === 'fijo') importeTexto = `${t('monto fijo')} ${fijo || '?'} cts`;
        else if (calculoTipo === 'mayor_de') importeTexto = `mayor_de(${pct}%, ${fijo || '?'} cts)`;
        else if (calculoTipo === 'menor_de') importeTexto = `menor_de(${pct}%, ${fijo || '?'} cts)`;

        const absorbeRadios = document.querySelectorAll('input[name="obligacionAbsorbe"]');
        let absorbeValor = 'operacion';
        for (let i = 0; i < absorbeRadios.length; i++) {
            if (absorbeRadios[i].checked) {
                absorbeValor = absorbeRadios[i].value;
                break;
            }
        }
        const absorbeTexto =
            { operacion: t('la operación'), jugador: t('el jugador'), condicional: t('condicional (umbral)') }[
                absorbeValor
            ] || absorbeValor;

        const orden = document.getElementById('obligacionOrden')?.value || '100';
        const heredable = document.getElementById('obligacionHeredable')?.checked;

        resumenEl.innerHTML = `
          <strong style="color:var(--text-primary);">${codigo}</strong> — ${nombre}<br>
          <span style="color:var(--text-tertiary);">${t('Tipo:')}</span> ${tipoLabel} &nbsp;·&nbsp;
          <span style="color:var(--text-tertiary);">${t('Cálculo:')}</span> ${importeTexto}${baseNeta ? ` <em>(${t('base neta')})</em>` : ''}<br>
          <span style="color:var(--text-tertiary);">${t('Paga:')}</span> ${absorbeTexto} &nbsp;·&nbsp;
          <span style="color:var(--text-tertiary);">${t('Orden:')}</span> ${orden}${heredable ? ` &nbsp;·&nbsp; <span style="color:var(--accent-green);">${t('heredable')}</span>` : ''}
        `;
    },

    async crearObligacion() {
        if (!this._contratoId) {
            App.toast(t('Guarde el contrato primero'), 'error');
            return;
        }

        // Paso 1 — Identificación
        const codigo = (document.getElementById('obligacionCodigo')?.value || '').trim().toUpperCase();
        const nombre = (document.getElementById('obligacionNombre')?.value || '').trim();
        const tipoHidden = document.getElementById('obligacionTipo');
        const tipoValorBase = tipoHidden?.value || 'impuesto_estatal';
        // Para "otro" usamos el texto libre si está relleno, si no el valor 'otro'
        const tipoOtroTexto = (document.getElementById('obligacionTipoOtroTexto')?.value || '').trim();
        const tipo = tipoValorBase === 'otro' && tipoOtroTexto ? tipoOtroTexto : tipoValorBase;
        const beneficiario_nombre = (document.getElementById('obligacionBeneficiario')?.value || '').trim() || null;

        if (!codigo || !nombre) {
            App.toast(t('Código y nombre son requeridos'), 'error');
            this._goToWizardStep(1);
            return;
        }

        // Paso 2 — Base
        const baseRadios = document.querySelectorAll('input[name="obligacionBase"]');
        let base = 'ggr';
        for (let i = 0; i < baseRadios.length; i++) {
            if (baseRadios[i].checked) {
                base = baseRadios[i].value;
                break;
            }
        }
        const base_neta = document.getElementById('obligacionBaseNeta')?.checked ? 1 : 0;
        const condicionValorRaw = document.getElementById('obligacionCondicionValor')?.value;
        const monto_minimo_aplicacion = condicionValorRaw ? parseInt(condicionValorRaw, 10) : null;

        // Paso 3 — Importe
        const calculo_tipo = document.getElementById('obligacionCalculoTipo')?.value || 'porcentaje';
        const porcentaje = parseFloat(document.getElementById('obligacionPorcentaje')?.value || '0') || 0;
        const montoFijoRaw = document.getElementById('obligacionMontoFijo')?.value;
        const monto_fijo = montoFijoRaw ? parseInt(montoFijoRaw, 10) : null;

        // Paso 4 — Quién absorbe
        const absorbeRadios = document.querySelectorAll('input[name="obligacionAbsorbe"]');
        let absorbe = 'operacion';
        for (let i = 0; i < absorbeRadios.length; i++) {
            if (absorbeRadios[i].checked) {
                absorbe = absorbeRadios[i].value;
                break;
            }
        }
        const absorbe_condicion_operador =
            absorbe === 'condicional' ? document.getElementById('obligacionAbsorbeOp')?.value || null : null;
        const absorbeValorRaw = document.getElementById('obligacionAbsorbeValor')?.value;
        const absorbe_condicion_valor =
            absorbe === 'condicional' && absorbeValorRaw ? parseInt(absorbeValorRaw, 10) : null;

        const orden_aplicacion = parseInt(document.getElementById('obligacionOrden')?.value || '100', 10);
        const heredable = document.getElementById('obligacionHeredable')?.checked ? 1 : 0;

        try {
            const res = await fetch(`/api/admin/contratos/${this._contratoId}/obligaciones`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-admin-token': App.getToken(),
                },
                body: JSON.stringify({
                    codigo,
                    nombre,
                    tipo,
                    beneficiario_nombre,
                    base,
                    base_neta,
                    monto_minimo_aplicacion,
                    calculo_tipo,
                    porcentaje,
                    monto_fijo,
                    absorbe,
                    absorbe_condicion_operador,
                    absorbe_condicion_valor,
                    orden_aplicacion,
                    heredable,
                }),
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Error al crear');
            }

            App.toast(`Obligacion "${nombre}" creada`, 'success');
            document.getElementById('obligacionFormContainer').style.display = 'none';
            this.loadObligaciones();
        } catch (err) {
            App.toast(err.message || t('Error al crear'), 'error');
        }
    },

    async eliminarObligacion(obligacionId, nombre) {
        if (!confirm(`${t('¿Eliminar obligacion "')}${nombre}"?`)) return;

        try {
            const res = await fetch(`/api/admin/contratos/${this._contratoId}/obligaciones/${obligacionId}`, {
                method: 'DELETE',
                headers: { 'x-admin-token': App.getToken() },
            });

            if (!res.ok) throw new Error(t('Error al eliminar'));

            App.toast(`"${nombre}" eliminada`, 'success');
            this.loadObligaciones();
        } catch (err) {
            App.toast(err.message || t('Error al eliminar'), 'error');
        }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // PRIVADOS SETUP — init de inputs y bindings al abrir el modal.
    // ═══════════════════════════════════════════════════════════════════════

    _setupModal(soloPromotor = false) {
        // Reconstruir el modal según el perfil del nodo. Un Promotor Digital solo
        // ve la pestaña "Comisiones" (las otras 8 son de un nodo que mueve dinero).
        // Reemplazamos el #modalContrato en el DOM por la versión correcta —
        // idempotente: si el flag no cambió respecto a la última apertura, no
        // reconstruye (evita re-render innecesario y pérdida de listeners).
        this._reconstruirModalSiHaceFalta(soloPromotor);

        // Reset al primer tab (comisiones es el default en ambos modos).
        this.setTab('comisiones');

        // Reset herencia indicator
        const herenciaEl = document.getElementById('contratoHerenciaIndicator');
        if (herenciaEl) herenciaEl.style.display = 'none';

        // Ocultar el wizard de obligaciones (puede quedar visible de una apertura anterior)
        const obligForm = document.getElementById('obligacionFormContainer');
        if (obligForm) obligForm.style.display = 'none';
    },

    /**
     * Reconstruye el HTML del #modalContrato con (o sin) el filtro de promotor,
     * solo si el modo cambió respecto a la última apertura. El modal se monta
     * una vez en el overlay con las 9 tabs; para un promotor lo reemplazamos por
     * la versión de 1 tab. Preserva display:none (el modal se muestra después).
     * @param {boolean} soloPromotor
     */
    _reconstruirModalSiHaceFalta(soloPromotor) {
        if (this._modoSoloPromotorActual === soloPromotor && document.getElementById('modalContrato')) {
            return; // mismo modo y modal presente → nada que reconstruir
        }
        const actual = document.getElementById('modalContrato');
        if (!actual) return; // el overlay aún no montó el modal (no debería pasar)

        // contratoModal() devuelve el <div id="modalContrato">…</div> completo.
        const temp = document.createElement('div');
        temp.innerHTML = contratoModal({ soloPromotor }).trim();
        const nuevo = temp.firstElementChild;
        if (nuevo) {
            nuevo.style.display = 'none'; // se abre con App.openModal después
            actual.replaceWith(nuevo);
            this._modoSoloPromotorActual = soloPromotor;
        }
    },

    _populateForm(data) {
        // Setter DEFENSIVO: cuando el modal está filtrado por perfil (Promotor
        // Digital ve SOLO la tab "Comisiones"), los inputs de las otras tabs NO
        // existen en el DOM. Si seteáramos .value sobre un getElementById null,
        // crashearía y el modal nunca abriría. Por eso cada set se salta si el
        // input no está. Mismo criterio defensivo que _collectFormData.
        const set = (id, value) => {
            const e = document.getElementById(id);
            if (e) e.value = value;
        };
        const check = (id, value) => {
            const e = document.getElementById(id);
            if (e) e.checked = !!value;
        };

        // --- TAB: COMISIONES (siempre presente) ---
        this._cargarAmortizacionEnPreset(data.porcentaje_amortizacion);

        // Comisión por canal (única fuente de verdad): JSON { <canal>: {porcentaje, base} }. Solo
        // wallet/ticket en la UI; un canal vacío = no cobra en ese canal. Backend valida claves canónicas.
        let porCanal = data.comision_por_canal || {};
        if (typeof porCanal === 'string') {
            try {
                porCanal = JSON.parse(porCanal);
            } catch {
                porCanal = {};
            }
        }
        for (const { id: canal } of CANALES_DE_COMISION_DEL_MODAL) {
            set(`contratoComision_${canal}_pct`, porCanal?.[canal]?.porcentaje ?? '');
            set(`contratoComision_${canal}_base`, porCanal?.[canal]?.base || 'ggr');
            // sin_cap es un checkbox (no .value): cargarlo y mostrar el aviso si viene marcado.
            const sinCapEl = document.getElementById(`contratoComision_${canal}_sincap`);
            const sinCapActivo = porCanal?.[canal]?.sin_cap === true;
            if (sinCapEl) sinCapEl.checked = sinCapActivo;
            const avisoEl = document.getElementById(`contratoComision_${canal}_sincap_aviso`);
            if (avisoEl) avisoEl.style.display = sinCapActivo ? 'block' : 'none';
        }
        this._aplicarCanalesDelNodo();

        // Periodicidad de pago: derivar el tipo de qué campo viene poblado.
        // Días corridos → semanal(7)/quincenal(15)/mensual(30). Cualquier otro N
        // (de datos legados o manuales) cae a 'mensual' como aproximación más cercana.
        const diasCorridos = data.periodicidad_pago_dias;
        const tieneDiasFijos = data.periodicidad_pago_dias_fijos != null;
        let tipoPeriodicidad = '';
        if (diasCorridos != null) {
            if (diasCorridos === 7) tipoPeriodicidad = 'semanal';
            else if (diasCorridos === 15) tipoPeriodicidad = 'quincenal';
            else tipoPeriodicidad = 'mensual';
        } else if (tieneDiasFijos) {
            tipoPeriodicidad = 'dias_fijos';
        }
        set('contratoPeriodicidadTipo', tipoPeriodicidad);
        if (tieneDiasFijos) {
            // El backend guarda JSON '[1,15]'; lo mostramos como '1, 15'.
            let diasFijos = data.periodicidad_pago_dias_fijos;
            try {
                diasFijos = JSON.parse(data.periodicidad_pago_dias_fijos).join(', ');
            } catch (_) {
                /* si no parsea, mostrar crudo */
            }
            set('contratoPeriodicidadDiasFijos', diasFijos);
        } else {
            set('contratoPeriodicidadDiasFijos', '');
        }
        this.togglePeriodicidad();

        // --- TAB: WALLET ---
        set('contratoNivelWallet', data.nivel_wallet || '');

        // --- TAB: INCENTIVOS + OBLIGACIONES (tolerantes: si el contenedor no
        // está, su loader no pinta nada — no crashea) ---
        this.loadIncentivos();
        this.loadObligaciones();

        // --- TAB: ADELANTOS ---
        set('contratoAdelantoLimite', data.adelanto_limite_mensual ?? '');

        // --- TAB: FONDO ---
        set('contratoFondoLimite', data.fondo_maniobra_limite ?? '');

        // --- TAB: CONFORMIDAD ---
        set('contratoConformidadPolitica', data.conformidad_politica || 'tiempo_real');
        set('contratoConformidadPlazo', data.conformidad_plazo_horas ?? '');
        this._toggleConformidadPlazoVisibility();
        set('contratoConformidadCanal', data.conformidad_canal_notificacion || 'sistema');
        set('contratoConformidadTelefonoWhatsapp', data.conformidad_telefono_whatsapp || '');
        if (document.getElementById('contratoLiquidacionNodoId')) {
            this._cargarNodosEnSelectLiquidacionNodo(data.liquidacion_nodo_id || null);
        }

        // --- TAB: PREPAGO ---
        const prepagoCheckbox = document.getElementById('contratoPrepago');
        if (prepagoCheckbox) {
            prepagoCheckbox.checked = !!data.prepago_habilitado;
            set('contratoLimiteCredito', data.limite_credito ?? '');
            set('contratoUmbralPremio', data.umbral_pago_directo ?? '');
            this._cargarAncestrosEnSelectUmbral(data.umbral_pago_nodo_id || null);
            this._togglePrepagoVisibility();
            this._toggleUmbralNodoVisibility();
            prepagoCheckbox.addEventListener('change', () => this._togglePrepagoVisibility());
            const umbralEl = document.getElementById('contratoUmbralPremio');
            if (umbralEl) umbralEl.addEventListener('input', () => this._toggleUmbralNodoVisibility());
        }

        // sin_cap por canal: el aviso de peligro aparece/desaparece en vivo al togglear el checkbox.
        for (const { id: canal } of CANALES_DE_COMISION_DEL_MODAL) {
            const chk = document.getElementById(`contratoComision_${canal}_sincap`);
            const aviso = document.getElementById(`contratoComision_${canal}_sincap_aviso`);
            if (chk && aviso) {
                chk.addEventListener('change', () => {
                    aviso.style.display = chk.checked ? 'block' : 'none';
                });
            }
        }

        // --- TAB: COMPLIANCE (§8 roadmap post-v1.0) ---
        check('contratoMayoriaEdadValidadaPorCajero', data.mayoria_edad_validada_por_cajero);

        // --- NOTAS (en la tab Comisiones — presente en ambos modos) ---
        set('contratoNotas', data.notas || '');
    },

    _collectFormData() {
        // Lectores DEFENSIVOS: cuando el modal está filtrado por perfil (Promotor
        // Digital ve SOLO la tab "Comisiones"), los inputs de las otras tabs NO
        // existen en el DOM. Un campo ausente → undefined, y al final lo quitamos
        // del payload. En el path de EDICIÓN, actualizarContrato usa _merge
        // (hasOwnProperty): una clave ausente PRESERVA el valor actual del
        // contrato; una clave presente con null lo limpia. Por eso quitar las
        // ausentes es lo correcto — editar la comisión de un promotor NO borra el
        // resto de su contrato. (En CREATE el contrato es nuevo: nada que preservar,
        // los ausentes toman CONTRATO_DEFAULTS.)
        const el = id => document.getElementById(id);
        const num = (id, parser) => {
            const e = el(id);
            if (!e) return undefined;
            const v = parser(e.value);
            return Number.isFinite(v) ? v : null;
        };
        const str = (id, fallback) => {
            const e = el(id);
            if (!e) return undefined;
            const v = (e.value || '').trim();
            return v || fallback;
        };
        const bool = id => {
            const e = el(id);
            return e ? (e.checked ? 1 : 0) : undefined;
        };

        const datos = {
            // Identificación — nodo_id requerido por backend (Nodos Fractales v4.0)
            nodo_id: this._hijoId,
            padre_entidad: this._padreEntidad,
            padre_id: this._padreId,
            hijo_entidad: this._hijoEntidad,
            hijo_id: this._hijoId,

            // Comisión: SOLO por canal. El escalar comision_porcentaje/comision_base ya no se emite
            // (SPEC_comision-solo-por-canal) — mandarlo crearía un % fantasma que nadie pactó en la UI y
            // que ningún canal usa. En CREATE el backend lo deja en CONTRATO_DEFAULTS (0, inerte); en
            // UPDATE la clave ausente preserva lo que ya hubiera, sin pisarlo con un valor inventado.
            // { <canal>: {porcentaje, base} } o undefined (ausente → preserva en edición).
            ...this._collectComisionPorCanal(el, this._mode),
            // GGR negativo: un solo % amortización (reemplaza las 3 políticas viejas). 0=la casa cubre,
            // 100=asume de una, N=cuotas. Leído del preset + el campo condicional.
            porcentaje_amortizacion: this._leerPorcentajeAmortizacionDelPreset(),

            // Periodicidad de pago (sección Comisiones, presente en ambos modos):
            // ...this._collectPeriodicidadPago() inserta periodicidad_pago_dias y
            // periodicidad_pago_dias_fijos según el tipo elegido (o ambos null).
            ...this._collectPeriodicidadPago(el),

            // Wallet
            nivel_wallet: str('contratoNivelWallet', null),

            // Adelantos
            adelanto_limite_mensual: num('contratoAdelantoLimite', parseInt),

            // Fondo de Maniobra
            fondo_maniobra_limite: num('contratoFondoLimite', parseInt),

            // Conformidad
            conformidad_politica: str('contratoConformidadPolitica', undefined),
            conformidad_plazo_horas: num('contratoConformidadPlazo', parseInt),
            conformidad_canal_notificacion: str('contratoConformidadCanal', 'sistema'),
            conformidad_telefono_whatsapp: str('contratoConformidadTelefonoWhatsapp', null),
            liquidacion_nodo_id: num('contratoLiquidacionNodoId', parseInt),

            // Prepago
            prepago_habilitado: bool('contratoPrepago'),
            limite_credito: num('contratoLimiteCredito', parseInt),
            umbral_pago_directo: num('contratoUmbralPremio', parseInt),
            umbral_pago_nodo_id: num('contratoUmbralPagoNodoId', parseInt),

            // Compliance (§8)
            mayoria_edad_validada_por_cajero: bool('contratoMayoriaEdadValidadaPorCajero'),

            // Notas
            notas: str('contratoNotas', null),
        };

        // Quitar las claves undefined (inputs ausentes por filtro de perfil) para
        // que el backend NO las pise. nodo_id/padre/hijo siempre van.
        for (const k of Object.keys(datos)) {
            if (datos[k] === undefined) delete datos[k];
        }

        return datos;
    },

    // ═══════════════════════════════════════════════════════════════════════
    // PRIVADOS UI HELPERS — formatters, toggles, validaciones inline.
    // ═══════════════════════════════════════════════════════════════════════

    // El campo "% que se salda por período" aparece SOLO cuando el preset es "cuotas". Mismo patrón display:none.
    _toggleAmortizacionCuotasVisibility() {
        const presetEl = document.getElementById('contratoAmortizacionPreset');
        const cuotasGroup = document.getElementById('amortizacionCuotasGroup');
        if (!presetEl || !cuotasGroup) return;
        cuotasGroup.style.display = presetEl.value === 'cuotas' ? 'block' : 'none';
    },

    // Mapea el porcentaje_amortizacion (0-100) del contrato al preset + campo: 0→"la casa cubre", 100→"asume de
    // una", intermedio→"cuotas" con el % en el campo. Default 100 (sin dato → asume de una, comportamiento previo).
    _cargarAmortizacionEnPreset(porcentaje) {
        const presetEl = document.getElementById('contratoAmortizacionPreset');
        const campoEl = document.getElementById('contratoPorcentajeAmortizacion');
        if (!presetEl) return;
        const pct = porcentaje == null ? 100 : Math.max(0, Math.min(100, Number(porcentaje)));
        if (pct <= 0) presetEl.value = '0';
        else if (pct >= 100) presetEl.value = '100';
        else {
            presetEl.value = 'cuotas';
            if (campoEl) campoEl.value = pct;
        }
        this._toggleAmortizacionCuotasVisibility();
    },

    // Lee el % amortización efectivo del preset + campo para guardar. "0"/"100" directos; "cuotas" → el campo
    // (clamp 1-99; vacío/inválido → 10 por defecto). Devuelve entero 0-100.
    _leerPorcentajeAmortizacionDelPreset() {
        const presetEl = document.getElementById('contratoAmortizacionPreset');
        if (!presetEl) return 100;
        if (presetEl.value === '0') return 0;
        if (presetEl.value === '100') return 100;
        const campoEl = document.getElementById('contratoPorcentajeAmortizacion');
        const v = campoEl ? parseInt(campoEl.value, 10) : NaN;
        if (!Number.isFinite(v)) return 10;
        return Math.max(1, Math.min(99, v));
    },

    // Muestra SOLO los campos de % de los canales que el nodo opera. Wallet siempre. Ticket solo si el nodo NO es
    // promotor (el promotor digital es solo-enlace/wallet por sistema) Y tiene sorteos asignados
    // (capacidadesDerivadas.tickets). Así un promotor no ve el campo Ticket → no lo setea por error.
    // Sin el dato (capacidades ausente y no-promotor) → no oculta nada (fallback conservador).
    _aplicarCanalesDelNodo() {
        // Muestra SOLO los canales que el nodo opera, según `capacidadesDerivadas` (SSOT del backend:
        // tickets ⇔ tiene sorteos vendibles, vlt ⇔ `vltConfigurable`: tiene terminales VLT, activas o no —
        // la comisión se pacta ANTES de activarlas—, b2b ⇔ acuerdo vigente).
        // wallet no declara capacidad: es universal.
        //
        // Ocultar NO borra el pacto. Antes se limpiaba el input al ocultarlo y, como
        // _collectComisionPorCanal omite del mapa los canales vacíos, guardar desde ese modal BORRABA la
        // comisión ya pactada de ese canal — un terminal VLT dado de baja un día se llevaba puesto el
        // acuerdo. Ahora el bloque se esconde y su valor viaja igual: desactivar una capacidad deja de
        // ver el canal, no de cobrarlo. Para dejar de cobrar hay que vaciar el % a mano, con el bloque
        // visible, que es una decisión explícita.
        const capacidades = this._capacidades;
        for (const canal of CANALES_DE_COMISION_DEL_MODAL) {
            const bloque = document.getElementById(`canalBloque_${canal.id}`);
            if (!bloque) continue;

            const visible = canalEsVisible(canal, capacidades, this._esPromotorActual);
            bloque.style.display = visible ? '' : 'none';
            bloque.dataset.canalOculto = visible ? '' : '1';
        }
    },

    // Días corridos que representa cada periodicidad fija. SSOT del mapeo
    // semanal/quincenal/mensual → N días.
    _DIAS_POR_PERIODICIDAD: { semanal: 7, quincenal: 15, mensual: 30 },

    // Muestra el input de días concretos solo cuando aplica (las periodicidades
    // fijas — semanal/quincenal/mensual — no piden input).
    togglePeriodicidad() {
        const tipo = document.getElementById('contratoPeriodicidadTipo')?.value || '';
        const fijosGroup = document.getElementById('periodicidadDiasFijosGroup');
        if (fijosGroup) fijosGroup.style.display = tipo === 'dias_fijos' ? 'block' : 'none';
    },

    /**
     * Recoge la periodicidad de pago del formulario según el tipo elegido. Devuelve
     * SIEMPRE ambos campos (el backend versiona el contrato): el no-elegido va null
     * para limpiarlo. Si la tab Comisiones no estuviera (no debería pasar), devuelve
     * objeto vacío → el backend preserva lo existente.
     */
    // Arma comision_por_canal con UN % por canal que el nodo opera. NO hay "default/general" — cada canal su %
    // (no se mezclan uvas con manzanas). wallet siempre; ticket solo si el nodo lo opera (no-promotor + tiene
    // sorteos). Si los inputs no están renderizados (perfil filtrado), devuelve {} (preserva lo existente).
    //
    // Sin ningún canal cargado, el shape depende del modo (hallazgo code-review PR-1
    // SPEC_comision-solo-por-canal.md): en CREATE hay que declarar backend `comision_por_canal: null`
    // para que el backend rechace con su mensaje claro (un contrato nuevo SIEMPRE debe tener ≥1 canal).
    // En UPDATE, la clave debe quedar AUSENTE (undefined, no null) — con la clave presente aunque sea
    // null, actualizarContrato entra a la rama de validación estricta y exige el canal incluso para
    // ediciones que no tocan comisión, congelando contratos legacy (comision_por_canal NULL en DB,
    // existen en prod) para SIEMPRE. `undefined` cae en el filtro de _collectFormData (línea ~1084) y
    // el backend conserva lo vigente, tal como documenta el comentario de _collectFormData arriba.
    _collectComisionPorCanal(el, modo) {
        if (!el('contratoComision_wallet_pct')) return {};
        // Esta función solo LEE el DOM; la regla de qué se pacta vive en shared/ y la comparten
        // producción y sus tests. Se leen TODOS los canales, incluidos los ocultos: ocultar no despacta
        // (ver construirComisionPorCanal).
        const valoresPorCanal = {};
        for (const { id: canal } of CANALES_DE_COMISION_DEL_MODAL) {
            valoresPorCanal[canal] = {
                valor: el(`contratoComision_${canal}_pct`)?.value || '',
                base: el(`contratoComision_${canal}_base`)?.value || 'ggr',
                sinCap: el(`contratoComision_${canal}_sincap`)?.checked === true,
            };
        }
        return construirComisionPorCanal(valoresPorCanal, modo);
    },

    _collectPeriodicidadPago(el) {
        const tipoEl = el('contratoPeriodicidadTipo');
        if (!tipoEl) return {};
        const tipo = tipoEl.value || '';
        // Periodicidades fijas (semanal/quincenal/mensual) → días corridos.
        if (this._DIAS_POR_PERIODICIDAD[tipo] != null) {
            return {
                periodicidad_pago_dias: this._DIAS_POR_PERIODICIDAD[tipo],
                periodicidad_pago_dias_fijos: null,
            };
        }
        if (tipo === 'dias_fijos') {
            const crudo = (el('contratoPeriodicidadDiasFijos')?.value || '').trim();
            // Parseamos a array de enteros 1-31; el backend re-valida y normaliza.
            const dias = crudo
                .split(',')
                .map(d => parseInt(d.trim(), 10))
                .filter(d => Number.isInteger(d) && d >= 1 && d <= 31);
            return {
                periodicidad_pago_dias: null,
                periodicidad_pago_dias_fijos: dias.length > 0 ? JSON.stringify(dias) : null,
            };
        }
        // Sin definir → limpiar ambos (hereda del padre).
        return { periodicidad_pago_dias: null, periodicidad_pago_dias_fijos: null };
    },

    _toggleConformidadPlazoVisibility() {
        // Defensivo: en el modal filtrado por perfil (Promotor Digital ve solo la
        // tab Comisiones) la tab Conformidad no existe → los elementos son null.
        const politicaEl = document.getElementById('contratoConformidadPolitica');
        const plazoGroup = document.getElementById('conformidadPlazoGroup');
        if (!politicaEl || !plazoGroup) return;
        plazoGroup.style.display = politicaEl.value === 'plazo_horas' ? 'block' : 'none';
    },

    _togglePrepagoVisibility() {
        const checked = document.getElementById('contratoPrepago')?.checked;
        const configGroup = document.getElementById('prepagoConfigGroup');
        if (configGroup) configGroup.style.display = checked ? 'block' : 'none';
    },

    _toggleUmbralNodoVisibility() {
        const umbralInput = document.getElementById('contratoUmbralPremio');
        const nodoGroup = document.getElementById('umbralPagoNodoGroup');
        if (!nodoGroup) return;
        const tieneUmbral = umbralInput && umbralInput.value && parseInt(umbralInput.value) > 0;
        nodoGroup.style.display = tieneUmbral ? 'block' : 'none';
    },

    async _cargarAncestrosEnSelectUmbral(selectedNodoId) {
        const select = document.getElementById('contratoUmbralPagoNodoId');
        if (!select) return;

        select.innerHTML = `<option value="">${t('Central (operador raiz)')}</option>`;

        if (!this._hijoId) return;

        try {
            const resp = await fetch(`${window.__API_ADMIN}/api/admin/nodos/${this._hijoId}`, {
                headers: { 'X-Admin-Token': window.__ADMIN_TOKEN },
            });
            if (!resp.ok) return;
            const data = await resp.json();
            const ancestors = data.nodo?.ancestros || [];
            for (let i = 0; i < ancestors.length; i++) {
                const a = ancestors[i];
                const tipoLabel = { operador: 'Op', representante: 'Rep', punto_venta: 'PV' }[a.tipo] || a.tipo;
                const opt = document.createElement('option');
                opt.value = a.id;
                opt.textContent = `${tipoLabel}: ${a.nombre}`;
                if (selectedNodoId && a.id === selectedNodoId) opt.selected = true;
                select.appendChild(opt);
            }
        } catch (_) {
            /* silencioso — el select queda con solo "Central" */
        }
    },

    _showHerenciaIndicator(heredadoDe) {
        const el = document.getElementById('contratoHerenciaIndicator');
        if (!el) return;

        if (!heredadoDe) {
            el.innerHTML = `<span style="color:var(--text-secondary);">${t('Usando valores por defecto del sistema')}</span>`;
        } else {
            // Backend devuelve { nodo_id, nombre, tipo } — normalizar campos
            const tipo = heredadoDe.tipo || heredadoDe.entidad;
            const id = heredadoDe.nodo_id || heredadoDe.id;
            const nombre = heredadoDe.nombre;
            const tipoLabel =
                {
                    operador: t('Operador'),
                    representante: t('Representante'),
                    punto_venta: t('Punto de Venta'),
                    loto37: 'LotoStream Pro',
                }[tipo] || tipo;

            el.innerHTML = `<span style="color:var(--accent-orange);">${t('Heredando de')} ${tipoLabel}${nombre ? ' ' + nombre : ' #' + id}</span>`;
        }
        el.style.display = 'block';
    },

    // ═══════════════════════════════════════════════════════════════════════
    // PRIVADOS API CALLS — fetches al backend agrupados por dominio.
    // ═══════════════════════════════════════════════════════════════════════

    async _fetchPadreInfo(entidad, id) {
        try {
            const res = await fetch(`/api/admin/contabilidad/padre?nodoId=${id}`, {
                headers: { 'x-admin-token': App.getToken() },
            });
            if (!res.ok) return null;
            return await res.json();
        } catch (e) {
            console.error('[ContratoModal] Error fetching padre:', e);
            return null;
        }
    },

    async _fetchContratoEfectivo(entidad, id) {
        try {
            const res = await fetch(`/api/admin/contabilidad/contrato-efectivo?nodoId=${id}`, {
                headers: { 'x-admin-token': App.getToken() },
            });
            if (!res.ok) return {};
            return await res.json();
        } catch (e) {
            console.error('[ContratoModal] Error fetching contrato efectivo:', e);
            return {};
        }
    },

    async _fetchContrato(contratoId) {
        try {
            const res = await fetch(`/api/admin/contratos/${contratoId}`, {
                headers: { 'x-admin-token': App.getToken() },
            });
            if (!res.ok) return null;
            return await res.json();
        } catch (e) {
            console.error('[ContratoModal] Error fetching contrato:', e);
            return null;
        }
    },

    async _createContrato(datos) {
        const res = await fetch('/api/admin/contratos', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-admin-token': App.getToken(),
            },
            body: JSON.stringify(datos),
        });

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || 'Error al crear contrato');
        }

        return await res.json();
    },

    async _updateContrato(datos) {
        const res = await fetch(`/api/admin/contratos/${this._contratoId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'x-admin-token': App.getToken(),
            },
            body: JSON.stringify(datos),
        });

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || 'Error al actualizar contrato');
        }

        return await res.json();
    },

    /**
     * Carga todos los nodos en el select de liquidacion_nodo_id.
     * Se llama desde _populateForm() cuando se abre el modal.
     * @param {number|null} valorActual - ID del nodo actualmente seleccionado
     */
    async _cargarNodosEnSelectLiquidacionNodo(valorActual) {
        const select = document.getElementById('contratoLiquidacionNodoId');
        if (!select) return;

        try {
            const res = await fetch('/api/admin/nodos', {
                headers: { 'x-admin-token': App.getToken() },
            });
            if (!res.ok) return;

            const data = await res.json();
            const nodos = data.nodos || [];

            // Primera opción: padre directo (comportamiento por defecto)
            let html = '<option value="">Padre directo (por defecto)</option>';
            for (let i = 0; i < nodos.length; i++) {
                const n = nodos[i];
                const selected = valorActual && n.id === valorActual ? 'selected' : '';
                html += `<option value="${n.id}" ${selected}>[${n.tipo}] ${n.nombre}</option>`;
            }
            select.innerHTML = html;
        } catch (_errorCargandoNodos) {
            // No bloquear el modal si falla la carga de nodos
        }
    },
};
